import React, { useState, useEffect } from 'react';
import Dropzone from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { uploadModuleIcon } from '../../Pages/Library/CustomModule/Store/CustomModule.action';
import Loader from 'Components/Utility/Loader/Loader';
import { BombiixModal } from '../BombiixModal';

interface SvgUploadModalProps {
  show: boolean;
  onHide: () => void;
  moduleId: string;
  onUploadSuccess?: () => void;
}

const SvgUploadModal: React.FC<SvgUploadModalProps> = ({ show, onHide, moduleId, onUploadSuccess }) => {
  const { t } = useTranslation('custom-module-system-canvas');
  const dispatch = useDispatch();
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (!show) {
      setFiles([]);
      setError('');
    }
  }, [show]);

  const onFileDrop = (acceptedFiles: File[], fileRejections: any[]) => {
    if (fileRejections.length) {
      setError(t('CUSTOM_MODULE.SVG_UPLOAD.ERROR_FILE_INVALID'));
    } else if (acceptedFiles.length > 0 && fileRejections.length === 0) {
      const file = acceptedFiles[0];
      if (!file || !file.name.toLowerCase().endsWith('.svg')) {
        setError(t('CUSTOM_MODULE.SVG_UPLOAD.ERROR_FILE_INVALID'));
        setFiles([]);
        return;
      }

      const fileSize = (file.size / 1024 / 1024).toFixed(0);
      if (parseFloat(fileSize) > 5) {
        setError(t('CUSTOM_MODULE.SVG_UPLOAD.ERROR_FILE_SIZE_EXCEED'));
        setFiles([]);
      } else {
        setError('');
        setFiles(
          acceptedFiles.map((file) =>
            Object.assign(file, {
              preview: URL.createObjectURL(file),
            }),
          ),
        );
      }
    }
  };

  useEffect(
    () => () => {
      files.forEach((file: any) => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    },
    [files],
  );

  const handleUpload = async () => {
    if (files.length === 0) {
      setError(t('CUSTOM_MODULE.SVG_UPLOAD.ERROR_FILE_REQUIRED'));
      return;
    }

    setIsUploading(true);
    try {
      const file = files[0];
      if (!file) {
        setError(t('CUSTOM_MODULE.SVG_UPLOAD.ERROR_FILE_REQUIRED'));
        setIsUploading(false);
        return;
      }

      await dispatch(uploadModuleIcon(moduleId, file, t) as any);
      if (onUploadSuccess) {
        onUploadSuccess();
      }
      onHide();
    } catch (err: any) {
      setError(err?.toString() || t('CUSTOM_MODULE.SVG_UPLOAD.UPLOAD_FAILED'));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <BombiixModal
      show={show}
      title={t('CUSTOM_MODULE.SVG_UPLOAD.MODAL_TITLE')}
      onClose={onHide}
      hideSave
      size="md"
      footer={
        <button type="button" className="bombiix-btn btn-blue" onClick={handleUpload} disabled={isUploading || files.length === 0}>
          {isUploading ? t('CUSTOM_MODULE.SVG_UPLOAD.UPLOADING') : t('COMMON_LABEL.UPLOAD', { ns: 'common' })}
        </button>
      }
      className="common-image-upload"
    >
      {isUploading && <Loader />}
      {!isUploading && (
        <>
          <Dropzone accept=".svg" onDrop={onFileDrop}>
            {({ getRootProps, getInputProps, isDragActive }) => (
              <section className="upload-image-section">
                <div className="w-100 text-center" {...getRootProps()}>
                  <input {...getInputProps()} />
                  {isDragActive ? (
                    <p className="mb-0">{t('CUSTOM_MODULE.SVG_UPLOAD.DROP_FILE_HERE')}</p>
                  ) : (
                    <p className="mb-0">{t('CUSTOM_MODULE.SVG_UPLOAD.DRAG_DROP_MESSAGE')}</p>
                  )}
                  {files.length > 0 && <p className="text-center mb-0 mt-2">{files[0]?.name}</p>}
                </div>

                <label className="upload-icon">
                  <input {...getInputProps()} type="file" accept=".svg" className="img-Upload" />
                  <a className="align-items-center justify-content-center">
                    <i className="icon-Upload-1" />
                  </a>
                </label>
              </section>
            )}
          </Dropzone>

          {error && (
            <div className="d-flex justify-content-center align-items-center mt-3">
              <span className="text-danger">{error}</span>
            </div>
          )}
        </>
      )}
    </BombiixModal>
  );
};

export default SvgUploadModal;
